(() => {
  'use strict';
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => [...c.querySelectorAll(s)];
  const rootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const route = path => `${rootPath()}${String(path).replace(/^\//, '')}`;
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const overlay = qs('[data-drawer-overlay]');
  const drawers = {};
  const drawerFocusableSelector = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
  let lastDrawerTrigger = null;
  let searchExpandedObserver = null;

  const syncClosedSearchExpandedState = () => {
    const searchDrawer = drawers.search || qs('[data-search-drawer]');
    if (!searchDrawer?.hidden) return;
    qsa('input[aria-expanded]', searchDrawer).forEach(input => {
      if (input.getAttribute('aria-expanded') !== 'false') input.setAttribute('aria-expanded', 'false');
    });
  };

  const observeClosedSearchExpandedState = () => {
    searchExpandedObserver?.disconnect();
    const searchDrawer = drawers.search;
    if (!searchDrawer) return;
    const inputs = qsa('input[aria-expanded]', searchDrawer);
    if (!inputs.length) return;
    searchExpandedObserver = new MutationObserver(syncClosedSearchExpandedState);
    inputs.forEach(input => searchExpandedObserver.observe(input, {
      attributes: true,
      attributeFilter: ['aria-expanded']
    }));
    syncClosedSearchExpandedState();
  };

  const setDrawerAccessibility = (drawer, open) => {
    if (!drawer) return;
    drawer.hidden = !open;
    drawer.inert = !open;
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    if (!open && drawer.matches('[data-search-drawer]')) syncClosedSearchExpandedState();
  };

  const setDrawerTriggers = (name, expanded) => {
    qsa(`[data-open-drawer="${name}"]`).forEach(trigger => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const drawer = drawers[name];
      if (drawer?.id) trigger.setAttribute('aria-controls', drawer.id);
    });
  };

  const syncDrawers = () => {
    drawers.menu = qs('[data-mobile-menu]');
    drawers.search = qs('[data-search-drawer]');
    drawers.cart = qs('[data-cart-drawer]');
    drawers.facets = qs('[data-mobile-facets]');

    Object.entries(drawers).forEach(([name, drawer]) => {
      if (!drawer) return;
      if (!drawer.id) drawer.id = `FoodNet${name.charAt(0).toUpperCase()}${name.slice(1)}Drawer`;
      const open = drawer.classList.contains('is-open');
      setDrawerAccessibility(drawer, open);
      setDrawerTriggers(name, open);
    });
    observeClosedSearchExpandedState();
  };
  syncDrawers();

  function closeDrawers({ restoreFocus = true } = {}) {
    Object.entries(drawers).forEach(([name, el]) => {
      if (!el) return;
      el.classList.remove('is-open');
      setDrawerAccessibility(el, false);
      setDrawerTriggers(name, false);
    });
    document.body.classList.remove('drawer-open');
    if (overlay) overlay.hidden = true;

    if (restoreFocus && lastDrawerTrigger?.isConnected && typeof lastDrawerTrigger.focus === 'function') {
      const target = lastDrawerTrigger;
      lastDrawerTrigger = null;
      window.setTimeout(() => target.focus({ preventScroll: true }), 0);
    } else if (!restoreFocus) {
      lastDrawerTrigger = null;
    }
  }
  function openDrawer(name, trigger = null) {
    syncDrawers();
    closeDrawers({ restoreFocus: false });
    const el = drawers[name];
    if (!el) return;
    lastDrawerTrigger = trigger || document.activeElement;
    setDrawerAccessibility(el, true);
    el.classList.add('is-open');
    setDrawerTriggers(name, true);
    document.body.classList.add('drawer-open');
    if (overlay) overlay.hidden = false;
    const focusable = el.querySelector(drawerFocusableSelector);
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 60);
  }
  overlay?.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', e => {
    const activeDrawer = Object.values(drawers).find(el => el && !el.hidden && el.classList.contains('is-open'));
    if (e.key === 'Escape' && activeDrawer) {
      e.preventDefault();
      closeDrawers();
      return;
    }
    if (e.key === 'Tab' && activeDrawer) {
      const focusable = qsa(drawerFocusableSelector, activeDrawer)
        .filter(el => !el.hidden && el.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!activeDrawer.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
    if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '')) {
      const search = drawers.search || qs('[data-search-drawer]');
      if (search) { e.preventDefault(); openDrawer('search'); }
    }
  });
  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-open-drawer]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.openDrawer, opener); return; }
    if (e.target.closest('[data-close-drawer]')) { e.preventDefault(); closeDrawers(); return; }

    const carouselButton = e.target.closest('[data-carousel-prev],[data-carousel-next]');
    if (carouselButton) {
      const carousel = carouselButton.closest('[data-carousel]');
      const track = carousel?.querySelector('[data-carousel-track]');
      if (track) {
        const direction = carouselButton.hasAttribute('data-carousel-prev') ? -1 : 1;
        track.scrollBy({ left: direction * track.clientWidth * .82, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      }
    }
  });

  const header = qs('[data-site-header]');
  if (header?.dataset.sticky === 'true') {
    const setSticky = () => header.classList.toggle('is-sticky', window.scrollY > 72);
    setSticky();
    window.addEventListener('scroll', setSticky, { passive: true });
  }

  qsa('[data-hero]').forEach(hero => {
    const slides = qsa('[data-hero-slide]', hero);
    if (slides.length < 2) return;
    let index = 0;
    slides.forEach((slide, i) => { slide.hidden = i !== 0; });
    const show = i => {
      index = (i + slides.length) % slides.length;
      slides.forEach((slide, j) => { slide.hidden = j !== index; });
    };
    qs('[data-hero-prev]', hero)?.addEventListener('click', () => show(index - 1));
    qs('[data-hero-next]', hero)?.addEventListener('click', () => show(index + 1));
    if (hero.dataset.autoplay === 'true' && !prefersReducedMotion) {
      const delay = Math.max(3000, Number(hero.dataset.delay || 6000));
      let timer = setInterval(() => show(index + 1), delay);
      hero.addEventListener('mouseenter', () => clearInterval(timer));
      hero.addEventListener('mouseleave', () => { timer = setInterval(() => show(index + 1), delay); });
    }
  });

  const wishlistKey = 'wokora-retail-wishlist';
  const readWishlist = () => { try { return JSON.parse(localStorage.getItem(wishlistKey) || '[]'); } catch { return []; } };
  const syncWishlist = () => {
    const list = readWishlist();
    qsa('[data-wishlist]').forEach(btn => {
      const active = list.includes(btn.dataset.wishlist);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };
  syncWishlist();
  document.addEventListener('shopify:section:load', syncWishlist);
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    let items = readWishlist();
    const handle = btn.dataset.wishlist;
    items = items.includes(handle) ? items.filter(x => x !== handle) : [...items, handle];
    localStorage.setItem(wishlistKey, JSON.stringify(items));
    syncWishlist();
  });

  document.addEventListener('click', e => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrapper = e.target.closest('[data-qty]');
    const input = wrapper?.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 1);
    const max = Number(input.max || 9999);
    const current = Number(input.value || min);
    input.value = minus ? Math.max(min, current - 1) : Math.min(max, current + 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
      }).format(value);
    } catch { return `€${value.toFixed(2)}`; }
  }

  async function getCart() {
    return fetch(route('cart.js'), { headers: { Accept: 'application/json' } }).then(r => r.json());
  }
  async function refreshCart(open = true) {
    try {
      const sections = await fetch(`${rootPath()}?sections=cart-drawer`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.json());
      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer]');
        const current = qs('[data-cart-drawer]');
        if (fresh && current) current.replaceWith(fresh);
      }
      syncDrawers();
      const cart = await getCart();
      qsa('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
      qsa('[data-header-cart-total]').forEach(el => { el.textContent = formatMoney(cart.total_price); });
      if (open) openDrawer('cart');
    } catch (err) { console.warn('WOKORA cart refresh failed', err); }
  }

  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-ajax-cart-form]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const oldText = button?.textContent;
    if (button) { button.disabled = true; button.textContent = '…'; }
    try {
      const res = await fetch(route('cart/add.js'), {
        method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: new FormData(form)
      });
      if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
      await refreshCart(true);
    } catch (err) {
      console.warn(err);
      form.submit();
      return;
    } finally {
      if (button) { button.disabled = false; button.textContent = oldText; }
    }
  });

  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-remove-line]');
    if (!btn) return;
    e.preventDefault();
    const line = Number(btn.dataset.removeLine);
    if (!line) return;
    btn.disabled = true;
    try {
      const res = await fetch(route('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity: 0 })
      });
      if (!res.ok) throw new Error('Unable to remove cart line');
      if (document.body.classList.contains('template-cart')) location.reload();
      else await refreshCart(false);
    } catch (err) { console.warn(err); btn.disabled = false; }
  });

  const searchInput = qs('[data-predictive-input]');
  const predictive = qs('[data-predictive-results]');
  let searchTimer;
  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  if (searchInput && predictive) {
    const emptyLabel = predictive.dataset.emptyLabel || 'No results found';
    const viewAllLabel = predictive.dataset.viewAllLabel || 'View all results';
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const q = searchInput.value.trim();
      if (q.length < 2) { predictive.innerHTML = ''; return; }
      searchTimer = setTimeout(async () => {
        try {
          const url = `${route('search/suggest.json')}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const data = await fetch(url).then(r => r.json());
          const products = data.resources?.results?.products || [];
          if (!products.length) { predictive.innerHTML = `<p>${escapeHtml(emptyLabel)}</p>`; return; }
          predictive.innerHTML = `<div class="predictive-grid">${products.map(p => `<a class="predictive-card" href="${escapeHtml(p.url)}"><img src="${escapeHtml(p.image || '')}" alt=""><div class="predictive-card__body"><div class="predictive-card__title">${escapeHtml(p.title)}</div><div class="predictive-card__price">${formatMoney(p.price)}</div></div></a>`).join('')}</div><div style="margin-top:14px"><a class="button button--outline" href="${escapeHtml(route(`search?q=${encodeURIComponent(q)}`))}">${escapeHtml(viewAllLabel)}</a></div>`;
        } catch { predictive.innerHTML = ''; }
      }, 220);
    });
  }

  qsa('[data-product-gallery]').forEach(gallery => {
    const main = qs('.product-main-gallery-image', gallery);
    qsa('[data-product-thumb]', gallery).forEach(btn => btn.addEventListener('click', () => {
      qsa('[data-product-thumb]', gallery).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      if (main) { main.src = btn.dataset.image; main.srcset = ''; main.alt = btn.dataset.alt || ''; }
    }));
  });

  qsa('[data-product-form]').forEach(form => {
    // FoodNet v7.7 is the canonical PDP variant/cart controller on current product forms.
    if (form.hasAttribute('data-foodnet-cart-bridge')) return;

    const root = form.closest('[data-product-root]') || document;
    const select = qs('[data-variant-select]', form);
    const optionInputs = qsa('[data-option-value]', form);
    if (!select || !optionInputs.length) return;
    let variants = [];
    try { variants = JSON.parse(qs('[data-variants-json]', form)?.textContent || '[]'); } catch { return; }
    const groups = [...new Set(optionInputs.map(input => input.name))];

    const applyVariant = variant => {
      if (!variant) return;
      select.value = variant.id;
      const available = Boolean(variant.available);
      const submit = qs('[type="submit"]', form);
      if (submit) {
        submit.disabled = !available;
        submit.textContent = available ? (submit.dataset.addLabel || 'In winkelwagen') : (submit.dataset.soldLabel || 'Niet beschikbaar');
      }
      const currentPrice = qs('[data-product-price-current]', root);
      const comparePrice = qs('[data-product-price-compare]', root);
      if (currentPrice) {
        currentPrice.textContent = formatMoney(variant.price);
        currentPrice.classList.toggle('price__sale', Number(variant.compare_at_price || 0) > Number(variant.price || 0));
      }
      if (comparePrice) {
        const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        comparePrice.hidden = !onSale;
        if (onSale) comparePrice.textContent = formatMoney(variant.compare_at_price);
      }
      const skuWrap = qs('[data-product-sku-wrap]', root);
      const sku = qs('[data-product-sku]', root);
      if (skuWrap && sku) { sku.textContent = variant.sku || ''; skuWrap.hidden = !variant.sku; }
      const stock = qs('[data-product-stock]', root);
      const stockLabel = qs('[data-product-stock-label]', root);
      const dot = qs('[data-stock-dot]', root);
      if (stock && stockLabel) stockLabel.textContent = available ? stock.dataset.inLabel : stock.dataset.outLabel;
      if (dot) dot.style.background = available ? '#62a52c' : '#b8bdb8';

      const featured = variant.featured_image?.src || variant.featured_image?.url;
      const mainImage = qs('.product-main-gallery-image', root);
      if (featured && mainImage) {
        mainImage.src = featured;
        mainImage.srcset = '';
      }
      const sticky = qs('[data-sticky-atc]');
      if (sticky) {
        const stickyId = qs('[data-sticky-variant-id]', sticky);
        const stickyPrice = qs('[data-sticky-price]', sticky);
        const stickyButton = qs('[type="submit"]', sticky);
        if (stickyId) stickyId.value = variant.id;
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        if (stickyButton) {
          stickyButton.disabled = !available;
          stickyButton.textContent = available ? (stickyButton.dataset.addLabel || 'In winkelwagen') : (stickyButton.dataset.soldLabel || 'Niet beschikbaar');
        }
      }
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    };

    const update = () => {
      const chosen = groups.map(name => form.querySelector(`[name="${CSS.escape(name)}"]:checked`)?.value);
      if (chosen.some(value => value == null)) return;
      const variant = variants.find(v => (v.options || []).every((option, i) => String(option) === String(chosen[i])));
      applyVariant(variant);
    };
    optionInputs.forEach(input => input.addEventListener('change', update));
    update();
  });
})();
